import type { Participant, RemoteAudioTrack } from 'livekit-client'
import { ref } from 'vue'
import { useLivekitConnection } from './useLivekitConnection'
import { useLivekitCore } from './useLivekitCore'

// Module-level singleton state
const listening = ref(false)
const currentListeningId = ref<string | null>(null)
const audioTracks = ref<Map<string, HTMLAudioElement>>(new Map())
const remoteTracks = ref<Map<string, RemoteAudioTrack>>(new Map())
const audioUpdateTick = ref(0)

export const cleanupLivekitAudio = () => {
  listening.value = false
  currentListeningId.value = null
}

export const useLivekitAudio = () => {
  const { connectToRoom, disconnectFromRoom } = useLivekitConnection()

  const attachAudioTrack = (track: RemoteAudioTrack, participant: Participant) => {
    remoteTracks.value.set(participant.identity, track)

    const audioEl = track.attach()
    audioEl.autoplay = true
    audioEl.muted = false
    audioEl.volume = 1
    audioEl.setAttribute('data-participant', participant.identity)

    audioTracks.value.set(participant.identity, audioEl)
    audioUpdateTick.value++

    audioEl.play().catch((err: unknown) => {
      if (import.meta.env.DEV) console.warn(`⚠️ play() failed for ${participant.identity}`, err)
    })
  }

  const detachAudioTrack = (participant: Participant) => {
    const el = audioTracks.value.get(participant.identity)
    if (el) {
      el.pause()
      el.remove()
      audioTracks.value.delete(participant.identity)
    }

    const track = remoteTracks.value.get(participant.identity)
    if (track) {
      track.detach()
      remoteTracks.value.delete(participant.identity)
    }

    if (currentListeningId.value === participant.identity) {
      currentListeningId.value = null
      listening.value = false
    }
  }

  const toggleListening = async (agentId?: string, keepConnection = false) => {
    if (!agentId) return
    const { room } = useLivekitCore()
    const roomName = agentId
    const current = currentListeningId.value

    if (current === agentId) {
      listening.value = false
      currentListeningId.value = null

      room.remoteParticipants.forEach((participant) => {
        participant.audioTrackPublications.forEach((publication) => {
          publication.setSubscribed(false)
        })
      })

      if (!keepConnection) await disconnectFromRoom()
      return
    }

    if (current && current !== agentId) {
      if (!keepConnection) await disconnectFromRoom()
    }

    currentListeningId.value = agentId
    listening.value = true

    await connectToRoom({ roomName })

    room.remoteParticipants.forEach((participant) => {
      participant.audioTrackPublications.forEach((publication) => {
        if ((publication.kind as string) === 'audio') {
          publication.setSubscribed(true)
        }
      })
    })
  }

  const cleanupAll = cleanupLivekitAudio

  return {
    audioTracks,
    remoteTracks,
    audioUpdateTick,
    listening,
    currentListeningId,
    attachAudioTrack,
    detachAudioTrack,
    toggleListening,
    cleanupAll,
  }
}
